import { z } from 'zod';

//
// Helpers
//

export const trimWhitespaces = (input: string | null): string | null => {
  if (input === null) return null;
  const textWithSingleSpaces = input.replace(/ +(?= )/g, '');
  return textWithSingleSpaces.trim();
};

/** Returns null for empty strings, otherwise returns the original value. */
export const emptyToNull = (value: string | null): string | null => {
  if (value === null) return null;
  return value.trim() === '' ? null : value;
};

/**
 * Cleans input to remove any invalid char.
 * @param input
 */
export const sanitise = (input: string | null): string | null => {
  if (input === null) return input;
  // if (typeof input === 'string') {
  // \p{L} matches any kind of letter from any language
  // \p{N} matches any numeric digit
  // \p{P} matches any punctuation character
  // ' ' allows for spaces. Don't use `\s` as this accepts new line `\n` and tab chars.
  // () allows for parentheses, eg Salad (v)
  // / allows for forward slash to divide words, eg meat/veg
  // ! allows for exclamation marks (to indicate things are important)
  // % allows for "50% full" for example
  // : allows for colon, eg Mains: Seafood
  // + allows for plus signs, eg Coke+Fanta
  // ' allows for apostrophes, eg Joe's Cafe
  // , allows for commas in lists, eg apples, oranges, pears
  // . allows for periods/full stops, eg A.B.C. Cafe
  // `g` flag makes the replacement global (all occurrences)
  // `u` flag enables full Unicode support, which is necessary for Chinese characters

  // Note: The default apostrophe on macOS is the straight apostrophe ('), while the default apostrophe on iOS is the right single quotation mark (’).
  // We replace the iOS right single quotation mark with the the straight apostrophe for consistency.
  return input.replace(/’/g, "'").replace(/[^\p{L}\p{N} ()&:/%!+',.]/gu, '');
};

export type Optional<T, K extends keyof T> = Omit<T, K> & Partial<T>;
export type AreEqual<T, U> = T extends U ? (U extends T ? true : false) : false;
/**
 * Checks that two types are equal.
 * Confirms that the extended Zod schema doesn't change the JSON shape.
 * @example assertTypes<AreEqual<Order, OrderValid>>();
 */
export function assertTypes<T extends true>() {}

//
// Primitive
//

/**
 * Price in cents.
 */
export const zPrice = z.number().int().nonnegative();

/**
 * Text
 * Use for names, titles, etc.
 * - Trims whitespace automatically (including between words).
 */
export const zText = z
  .string()
  .transform(sanitise)
  .transform(trimWhitespaces)
  // It's important that `transform()` is NOT the last step and we pipe to `z.string()` so that Convex
  // can define the correct type for the field (and define it as `any`).
  .pipe(z.string().min(1, 'Required'))
  .pipe(z.string());

/**
 * Text nullable
 * - Normalizes empty strings to `null` for database storage.
 */
export const zTextNullable = z.string().nullable().transform(emptyToNull).pipe(zText.nullable());
